import * as React from 'react';

import {
  enumVariableSortOption,
  VariableSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  DataGrid,
  ListScreenLayout,
  Pagination,
  SearchField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import * as state from '../../_state/variablesList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { VariableRecord } from '../../types/pricing';

import { useEditableVariableTable } from './hooks/useEditableVariableTable';
import { useVariableColumns } from './hooks/useVariableColumns';
import { VariableFormDialog } from './VariableFormDialog';
import { VariableHistoryDialog } from './VariableHistoryDialog';
import { VariableSaveToolbar } from './VariableSaveToolbar';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: VariableSortOption; desc: VariableSortOption }
> = {
  name: {
    asc: enumVariableSortOption.NameAsc,
    desc: enumVariableSortOption.NameDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ VARIABLE LIST VIEW ⚛️ ⚛️ ⚛️
//
export const VariableListView = () => {
  // Translations
  const t = useTranslations();

  // Atoms
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 300);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );
  const [isEditDialogOpen, setIsEditDialogOpen] = useAtom(
    state.$isEditDialogOpen,
  );
  const [selectedVariable, setSelectedVariable] = useAtom(
    state.$selectedVariable,
  );
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useAtom(
    state.$isHistoryDialogOpen,
  );
  const [selectedHistoryVariableId, setSelectedHistoryVariableId] = useAtom(
    state.$selectedHistoryVariableId,
  );
  const [selectedHistoryVariableName, setSelectedHistoryVariableName] = useAtom(
    state.$selectedHistoryVariableName,
  );
  const setCurrentSorting = useSetAtom(state.$currentSorting);
  const deleteVariable = useSetAtom(state.$deleteVariable);
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteVariables,
  );

  // Data hooks
  const toast = useToast();

  const {
    pendingChanges,
    pendingCount,
    effectiveDate,
    effectiveTime,
    setEffectiveDate,
    setEffectiveTime,
    addPendingChange,
    clearPendingValue,
    revertChange,
    revertAll,
    activate,
  } = useEditableVariableTable();

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumVariableSortOption.NameAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleCreateClick = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  const handleEdit = React.useCallback(
    (row: VariableRecord) => {
      setSelectedVariable(row);
      setIsEditDialogOpen(true);
    },
    [setSelectedVariable, setIsEditDialogOpen],
  );

  const handleEditClose = React.useCallback(() => {
    setIsEditDialogOpen(false);
    setSelectedVariable(null);
  }, [setIsEditDialogOpen, setSelectedVariable]);

  const handleHistory = React.useCallback(
    (row: VariableRecord) => {
      setSelectedHistoryVariableId(row.id);
      setSelectedHistoryVariableName(row.name);
      setIsHistoryDialogOpen(true);
    },
    [
      setSelectedHistoryVariableId,
      setSelectedHistoryVariableName,
      setIsHistoryDialogOpen,
    ],
  );

  const handleDelete = React.useCallback(
    async (id: string) => {
      try {
        await deleteVariable({ id });
        toast.success(t.pricing.variables.toast.deleted);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        toast.error(t.pricing.variables.toast.deleteError(errorMessage));
      }
    },
    [deleteVariable, toast, t],
  );

  const { columns, columnVisibilityState } = useVariableColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
    onHistory: handleHistory,
    onPendingChange: addPendingChange,
    onClearValue: clearPendingValue,
  });

  const variables = data?.variables ?? [];
  const total = data?.totalCount ?? 0;
  const emptyPanelProps = {
    title: t.pricing.variables.list.empty.title,
    description: <span>{t.pricing.variables.list.empty.description}</span>,
    action: (
      <Button
        onPress={handleCreateClick}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.pricing.variables.list.createButton}
      </Button>
    ),
    centered: true,
  };

  return (
    <>
      <ListScreenLayout
        filters={
          <SearchField
            autoFocus
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value ?? '');
              setOffset(0);
            }}
            placeholder={t.pricing.variables.list.search}
            aria-label={t.pricing.variables.list.ariaLabel}
            className="w-64"
          />
        }
        actions={
          <Button
            onPress={handleCreateClick}
            variant="solid"
            color="primary"
            icon={<LucidePlusCircle />}
          >
            {t.pricing.variables.list.createButton}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
        className="@VariableListView"
      >
        <DataGrid<VariableRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          columnVisibilityState={columnVisibilityState}
          dataSource={variables}
          rowKey="id"
          aria-label={t.pricing.variables.list.ariaLabel}
          onColumnSort={handleColumnSort}
          className="w-full text-sm"
          fullWidth
          persistColumnOrderKey="brokerage:variables-list"
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.common.errors.loading(
                    'variables',
                    error?.message ?? 'Unknown error',
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <VariableSaveToolbar
        pendingCount={pendingCount}
        pendingChanges={pendingChanges}
        effectiveDate={effectiveDate}
        effectiveTime={effectiveTime}
        onDateChange={setEffectiveDate}
        onTimeChange={setEffectiveTime}
        onActivate={activate}
        onRevertAll={revertAll}
        onRevertChange={revertChange}
      />

      <VariableFormDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={() => refetch?.()}
      />

      <VariableFormDialog
        isOpen={isEditDialogOpen}
        onClose={handleEditClose}
        onSuccess={() => refetch?.()}
        variable={selectedVariable ?? undefined}
      />

      <VariableHistoryDialog
        isOpen={isHistoryDialogOpen}
        onClose={() => setIsHistoryDialogOpen(false)}
        variableId={selectedHistoryVariableId ?? ''}
        variableName={selectedHistoryVariableName ?? ''}
      />
    </>
  );
};
